import React, { useEffect, useState, useContext, useRef } from "react";
import { useLocation } from "react-router-dom";
import banner from "../../assets/banner/lighting-banner.avif";
import kitchenBanner from "../../assets/banner/kitchen-banner.avif";
import homeLivingBanner from "../../assets/banner/home-living-banner.avif";
import accessoriesBanner from "../../assets/EVAWallet.avif";
import moonlineBanner from "../../assets/banner/moonline-banner.avif";
import DeskWare from "../../assets/DeskWare.avif";

import ProductCard from "../../components/ProductCard";
import styles from "./style.module.scss";
import Footer from "../../components/Footer";
import DropdownMenu from "../../components/DropdownMenu";
import { collection, getDocs } from "firebase/firestore/lite";
import { db } from "../../Firebase";
import { ShopContext } from "../../context/ShopContext";
import { gsap } from "gsap";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import SEO from "../../context/SEO";
import FAQ from "../../components/FAQ";

gsap.registerPlugin(ScrollToPlugin);

function AllProducts() {
  const [showElements, setShowElements] = useState("full-opacity");
  const prevScrollPos = useRef(window.scrollY); // Critical fix: useRef!
  const [vh, setVh] = useState(window.innerHeight * 0.01);

  const [todos, setTodos] = useState([]);
  const { products } = useContext(ShopContext);
  const location = useLocation();

  // Update vh on resize (mobile rotation, etc.)
  useEffect(() => {
    const updateVh = () => setVh(window.innerHeight * 0.01);
    updateVh();
    window.addEventListener("resize", updateVh);
    return () => window.removeEventListener("resize", updateVh);
  }, []);

  // Scroll handler — now works perfectly!
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos = window.scrollY;
      const scrollingUp = currentScrollPos < prevScrollPos.current;
      const scrollingDown = currentScrollPos > prevScrollPos.current;
      const atTop = currentScrollPos < 10 * vh;

      if (atTop) {
        setShowElements("full-opacity");
      } else if (scrollingUp) {
        setShowElements("fade-in-up");     // Appears when scrolling up!
      } else if (scrollingDown) {
        setShowElements("fade-out-up");    // Hides when scrolling down
      }

      // Update ref immediately — no delay!
      prevScrollPos.current = currentScrollPos;
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [vh]);

  // Optional: Fetch from Firestore (you had this)
  const fetchPost = async () => {
    await getDocs(collection(db, "products")).then((querySnapshot) => {
      const newData = querySnapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      }));
      setTodos(newData);
    });
  };

  useEffect(() => {
    fetchPost();
  }, []);

  // Smooth scroll to section on hash change
  useEffect(() => {
    if (location.hash) {
      const hash = location.hash.slice(1);
      const elem = document.getElementById(hash);
      if (elem) {
        const targetPosition = elem.getBoundingClientRect().top + window.scrollY - 80;
        const distance = Math.abs(targetPosition - window.scrollY);
        const duration = 1.3 + distance / 3800;

        gsap.to(window, {
          delay: 0.5,
          duration: duration > 3 ? 3 : duration,
          scrollTo: { y: elem, offsetY: 80 },
          ease: "power2.inOut",
        });
      }
    }
  }, [location]);

  // Filter products
  const lightingProducts = products.filter((p) => p.collection.includes("Lighting"));
  const officeProducts = products.filter((p) => p.collection.includes("Office"));
  const kitchenProducts = products.filter((p) => p.collection.includes("Kitchen"));
  const homeProducts = products.filter((p) => p.collection.includes("Home"));
  const moonlineProducts = products.filter((p) => p.collection.includes("Moonline"));
  const EVAProducts = products.filter((p) => p.collection.includes("EVA"));

  return (
    <div className={styles.collectionDiv}>
      <SEO title="Cork Matters - All Products" />
      <DropdownMenu showElements={showElements} />

      {/* Lighting */}
      <div className={styles.banner}>
        <img src={banner} alt="Lighting Collection" />
        <div className={styles.sectionTitleOnImage} id="lighting">
          <p>LIGHTING COLLECTION</p>
        </div>
      </div>
      <div className={styles.section}>
        <div className={styles.grid}>
          {lightingProducts.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      </div>

      {/* Office */}
      <div className={styles.banner}>
        <img src={DeskWare} alt="Office Collection" />
        <div className={styles.sectionTitleOnImage} id="office">
          <p>OFFICE</p>
        </div>
      </div>
      <div className={styles.section}>
        <div className={styles.grid}>
          {officeProducts.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      </div>

      {/* Tableware */}
      <div className={styles.banner}>
        <img src={kitchenBanner} alt="Tableware" id="tableware" />
        <div className={`${styles.sectionTitleOnImage} ${styles.blackTitle}`}>
          <p>TABLEWARE</p>
        </div>
      </div>
      <div className={styles.section}>
        <div className={styles.grid}>
          {kitchenProducts.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      </div>

      {/* Home Living */}
      <div className={styles.banner}>
        <img src={homeLivingBanner} alt="Home Living" id="home-living" />
        <div className={styles.sectionTitleOnImage}>
          <p>HOME LIVING</p>
        </div>
      </div>
      <div className={styles.section}>
        <div className={styles.grid}>
          {homeProducts.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      </div>

      {/* Moonline */}
      <div className={styles.banner}>
        <img src={moonlineBanner} alt="Moonline" id="moonline" />
        <div className={styles.sectionTitleOnImage}>
          <p>MOONLINE</p>
        </div>
      </div>
      <div className={styles.section}>
        <div className={styles.grid}>
          {moonlineProducts.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      </div>

      {/* EVA */}
      <div className={styles.banner}>
        <img src={accessoriesBanner} alt="EVA Collection" id="eva-collection" />
        <div className={styles.sectionTitleOnImage}>
          <p>EVA</p>
        </div>
      </div>
      <div className={styles.section}>
        <div className={styles.grid}>
          {EVAProducts.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      </div>

      <FAQ />
      <Footer />
    </div>
  );
}

export default AllProducts;